import styled from "@emotion/styled";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button as AIMEButton } from "tot-loft-component-library";
import {
  OutlineEye,
  OutlineEyeOff,
  PaartnerLogo,
  PaartnerLogo2,
} from "./assets";
import * as PRTNR from "./prtnr";
import { SetupNewPassword } from "./redux/slices/authThunks";
import { validatePassword } from "./utils/func";

export default function NewPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const uid = params.get("uid");
  const token = params.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset errors first
    setPasswordError("");
    setConfirmPasswordError("");

    const passwordCheck = validatePassword(password);

    if (!passwordCheck.valid) {
      setPasswordError(passwordCheck.message);
      return;
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Confirm your password");
      return;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    try {
      await dispatch(
        SetupNewPassword({
          userId: uid,
          token: token,
          newPassword: password,
        })
      ).unwrap();
      navigate("/login");
    } catch (err) {
      console.warn(err.message || "Failed to reset password");
      console.log(err);

      // Handle API error response with "message" field
      const message = err || "Something went wrong. Please try again.";
      setApiError(message);
    }
  };

  return (
    <RootWrapperHome>
      <Section>
        <Wrapper>
          <SignupSignin>
            <ContentBlock1>
              <Content>
                <EarningCashForMakingReferralsIsEasierThanEver>
                  New Password
                </EarningCashForMakingReferralsIsEasierThanEver>
                <DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed>
                  Please create a new password.
                </DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed>
              </Content>
            </ContentBlock1>
            <Frame58>
              <PRTNR.Input
                label="Password"
                value={password}
                type={showPassword ? "text" : "password"}
                icon={showPassword ? OutlineEye : OutlineEyeOff}
                onIconClick={() => setShowPassword(!showPassword)}
                onChange={(e) => {
                  const val = e.target.value;
                  setPassword(val);

                  // Live clear error
                  if (passwordError) {
                    validatePassword(val);
                  }
                }}
                required
                error={!!passwordError}
                errorMessage={passwordError}
              />

              <PRTNR.Input
                label="Confirm password"
                value={confirmPassword}
                type={showConfirmPassword ? "text" : "password"}
                icon={showConfirmPassword ? OutlineEye : OutlineEyeOff}
                onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                onChange={(e) => {
                  const val = e.target.value;
                  setConfirmPassword(val);

                  // Live clear error
                  if (confirmPasswordError) {
                    if (val === password) {
                      setConfirmPasswordError("");
                    } else {
                      setConfirmPasswordError("Passwords do not match");
                    }
                  }
                }}
                required
                error={!!confirmPasswordError}
                errorMessage={confirmPasswordError}
              />
              {apiError && <ErrorMessage>{apiError}</ErrorMessage>}

              <Frame59>
                <AIMEButton
                  size="small"
                  onClick={(e) => handleSubmit(e)}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                    flex: "none",
                    gap: "10px",
                    borderRadius: "48px",
                    width: "320px",
                    background: "rgb(33, 37, 67)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    boxSizing: "border-box",
                    padding: "16px 32px",
                    "&:hover": {
                      background: "rgb(33, 37, 67)",
                      opacity: "0.9",
                    },
                    "@media (max-width: 768px)": {
                      width: "100%",
                    },
                    "@media (max-width: 480px)": {
                      width: "100%",
                    },
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      textOverflow: "ellipsis",
                      fontSize: "16px",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "600",
                      lineHeight: "110%",
                      textAlign: "left",
                    }}
                  >
                    Create new password
                  </span>
                </AIMEButton>
                <AIMEButton
                  size="small"
                  onClick={() => navigate("/login")}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                    flex: "none",
                    gap: "10px",
                    borderRadius: "48px",
                    width: "320px",
                    background: "#E2E8F0",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    boxSizing: "border-box",
                    padding: "16px 32px",
                    "&:hover": {
                      background: "#E2E8F0",
                      opacity: "0.9",
                    },
                    "@media (max-width: 768px)": {
                      width: "100%",
                    },
                    "@media (max-width: 480px)": {
                      width: "100%",
                    },
                  }}
                >
                  <span
                    style={{
                      color: "#212543",
                      textOverflow: "ellipsis",
                      fontSize: "16px",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "600",
                      lineHeight: "110%",
                      textAlign: "left",
                    }}
                  >
                    Back to Login
                  </span>
                </AIMEButton>
              </Frame59>
            </Frame58>
          </SignupSignin>
        </Wrapper>
      </Section>
    </RootWrapperHome>
  );
}

const RootWrapperHome = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  /* min-height: 100vh; */
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Section = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 8px;
  align-self: stretch;
  height: 882px;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  box-sizing: border-box;
`;

const SignupSignin = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ContentBlock1 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 560px;
  box-sizing: border-box;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const EarningCashForMakingReferralsIsEasierThanEver = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(24px, 2.5vw, 48px);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: -1px;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(16px, 2vw, 20px);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame58 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 24px;
  border: solid 1px rgba(136, 136, 136, 0.1);
  border-radius: 32px;
  width: 560px;
  background: rgb(226, 232, 240);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: clamp(16px, 2vw, 32px);
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const PrtnrInput = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GFieldsStacked = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame5 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const FieldLabel = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Field = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  border: solid 1px rgb(224, 228, 244);
  border-radius: 8px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 8px 16px;
  flex-shrink: 0;
`;

const Placeholder = styled.span`
  color: rgb(179, 182, 195);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 465px;
  flex: 1;
`;

const PrtnrInputPassword = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Placeholder_0001 = styled.span`
  color: rgb(179, 182, 195);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  width: 441px;
  flex: 1;
`;

const Frame61 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame60 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 244px;
  box-sizing: border-box;
`;

const ButtonsCheckBox = styled.img`
  width: 24px;
  height: 24px;
  object-fit: cover;
`;

const RememberMe = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: center;
`;

const Frame62 = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 244px;
  box-sizing: border-box;
`;

const Frame59 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ButtonsApp = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 10px;
  border-radius: 48px;
  width: 320px;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px 32px;
`;

const ButtonLabel = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 110%;
  text-align: left;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Section_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Section_0002 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Wrapper_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame38 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const _2025PaartnerLimitedAllRightsReserved = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;
const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: left;
  width: 100%;
`;
